import type { KeyboardEvent } from "react";
import type { AudioPlayerMenuAction } from "../../types/audio-player";

import { useEffect, useId, useRef, useState } from "react";
import { AudioPlayerMenuItem } from "./AudioPlayerMenuItem";

type AudioPlayerMenuProps = {
  disabled?: boolean;
  menuActions: AudioPlayerMenuAction[];
};

function MenuDotsGlyph() {
  return (
    <svg
      aria-hidden="true"
      className="h-[22px] w-[46px]"
      fill="currentColor"
      viewBox="0 0 46 22"
    >
      <circle cx="11" cy="11" r="3" />
      <circle cx="23" cy="11" r="3" />
      <circle cx="35" cy="11" r="3" />
    </svg>
  );
}

function AudioPlayerMenu({
  disabled = false,
  menuActions,
}: AudioPlayerMenuProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuId = useId();
  const menuTriggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuItemRefs = useRef<Array<HTMLAnchorElement | HTMLButtonElement>>([]);
  const [menuAnnouncement, setMenuAnnouncement] = useState("");

  useEffect(() => {
    if (!isMenuOpen) {
      return;
    }

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target;

      if (
        target instanceof Node &&
        !menuRef.current?.contains(target) &&
        !menuTriggerRef.current?.contains(target)
      ) {
        setIsMenuOpen(false);
      }
    };
    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setIsMenuOpen(false);
        menuTriggerRef.current?.focus();
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isMenuOpen]);

  useEffect(() => {
    if (isMenuOpen) {
      menuItemRefs.current[0]?.focus();
    }
  }, [isMenuOpen]);

  function handleMenuKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const currentIndex = menuItemRefs.current.findIndex(
      (item) => item === document.activeElement,
    );

    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const direction = event.key === "ArrowDown" ? 1 : -1;
      const nextIndex =
        (currentIndex + direction + menuActions.length) % menuActions.length;
      menuItemRefs.current[nextIndex]?.focus();
    }

    if (event.key === "Home" || event.key === "End") {
      event.preventDefault();
      menuItemRefs.current[
        event.key === "Home" ? 0 : menuActions.length - 1
      ]?.focus();
    }
  }

  function handleMenuAction(action: AudioPlayerMenuAction) {
    setIsMenuOpen(false);
    menuTriggerRef.current?.focus();
    void Promise.resolve()
      .then(() => action.onSelect?.())
      .then((announcement) => {
        if (announcement) {
          setMenuAnnouncement(announcement);
        }
      })
      .catch(() => {
        setMenuAnnouncement("The action could not be completed.");
      });
  }

  if (menuActions.length === 0) {
    return null;
  }

  return (
    <>
      <div aria-live="polite" className="sr-only">
        {menuAnnouncement}
      </div>

      <div className="relative hidden shrink-0 sm:block">
        <button
          aria-controls={menuId}
          aria-expanded={isMenuOpen}
          aria-haspopup="menu"
          aria-label="More audio options"
          className="flex h-[22px] w-[46px] items-center justify-center rounded-full bg-new-beginnings-tints-800 transition-colors hover:bg-new-beginnings-tints-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-new-beginnings-base disabled:cursor-not-allowed disabled:opacity-50"
          disabled={disabled}
          onClick={() => setIsMenuOpen((isOpen) => !isOpen)}
          ref={menuTriggerRef}
          type="button"
        >
          <MenuDotsGlyph />
        </button>

        {isMenuOpen ? (
          <div
            aria-label="Audio options"
            className="absolute right-0 bottom-full z-10 mb-2 flex w-[200px] flex-col gap-2 rounded-2xl bg-[#2c4f26] p-2 text-[#f6ffe3] shadow-[0_8px_24px_rgba(44,79,38,0.2)]"
            id={menuId}
            onKeyDown={handleMenuKeyDown}
            ref={menuRef}
            role="menu"
          >
            {menuActions.map((action, index) => (
              <AudioPlayerMenuItem
                action={action}
                itemRef={(element) => {
                  if (element) {
                    menuItemRefs.current[index] = element;
                  }
                }}
                key={action.label}
                onSelect={handleMenuAction}
              />
            ))}
          </div>
        ) : null}
      </div>
    </>
  );
}

export { AudioPlayerMenu };
